<template>
    <v-dialog v-model="dialogProxy" max-width="780px" persistent scrollable>
        <v-card class="search-response-entities-card">
            <v-card-title class="py-3 white--text primarycolor d-flex align-center">
                <v-icon color="white" class="mr-2">mdi-magnify-plus-outline</v-icon>
                <div>
                    <div class="text-subtitle-1 font-weight-medium">Search response entities</div>
                    <div class="caption">Add products or brands outside the tracked top 200</div>
                </div>
                <v-spacer />
                <v-btn icon @click="closeDialog" aria-label="Close search response entities dialog">
                    <v-icon color="white">mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text class="pt-4 px-6 pb-2">
                <div class="caption grey--text text--darken-1 mb-5 mt-2">
                    We only track the top 200 brands detected in responses — products aren't tracked at all. Use this search to find specific products or brands that aren't already in your list and
                    add them manually.
                </div>

                <v-text-field
                    v-model="entitySearchQuery"
                    dense
                    outlined
                    clearable
                    hide-details
                    :loading="entitySearchLoading"
                    prepend-inner-icon="mdi-magnify"
                    label="Search response entities"
                    @click:clear="resetSearch" />

                <div v-if="entitySearchQuery && entitySearchQuery.length >= 2" class="mt-3 entity-search-results">
                    <div v-if="entitySearchLoading" class="caption grey--text text--darken-1 px-1 py-2">
                        <v-progress-circular indeterminate size="14" width="2" class="mr-2" /> Searching response entities…
                    </div>
                    <div v-else-if="entitySearchError" class="caption error--text px-1 py-2">{{ entitySearchError }}</div>
                    <div v-else-if="!entitySearchResults.length" class="caption grey--text text--darken-1 px-1 py-2">No matching response entities found.</div>
                    <v-card v-else outlined class="entity-search-card">
                        <div class="d-flex align-center px-3 py-2 entity-search-card__header flex-wrap">
                            <div class="d-flex align-center">
                                <span class="text-subtitle-2 font-weight-medium mr-2">{{ entitySearchResults.length }} result{{ entitySearchResults.length === 1 ? "" : "s" }}</span>
                                <v-chip x-small color="grey lighten-3" class="mr-2">
                                    <v-icon x-small left>mdi-check</v-icon>
                                    {{ alreadyManagedCount }} managed
                                </v-chip>
                                <v-chip x-small color="primary lighten-4" class="primary--text">
                                    <v-icon x-small left>mdi-plus</v-icon>
                                    {{ addableEntityResults.length }} addable
                                </v-chip>
                            </div>
                            <v-spacer />
                            <v-btn
                                v-if="addableEntityResults.length > 1"
                                small
                                rounded
                                dark
                                color="black"
                                class="mr-2 mb-1 white--text"
                                @click="openAddAllAsBrandDialog"
                                title="Add all results as a single brand (with each entity as a mention)">
                                <v-icon small left>mdi-plus-box-multiple</v-icon>
                                Add all as one brand
                            </v-btn>
                            <v-btn
                                v-if="addableEntityResults.length"
                                small
                                rounded
                                dark
                                color="black"
                                class="mb-1 white--text"
                                @click="whitelistAllEntitiesAsBrands"
                                title="Add each result as a separate brand">
                                <v-icon small left>mdi-plus-circle-multiple-outline</v-icon>
                                Add all as separate
                            </v-btn>
                        </div>
                        <v-divider />
                        <div v-if="!visibleResults.length" class="caption grey--text text--darken-1 px-3 py-3">All matching entities are already managed.</div>
                        <v-list v-else dense class="entity-search-list py-0">
                            <v-list-item
                                v-for="entity in visibleResults"
                                :key="`entity-row-${entity.name}`"
                                class="entity-search-row"
                                :class="{ 'entity-search-row--managed': isEntityAlreadyManaged(entity.name) }">
                                <v-list-item-content>
                                    <v-list-item-title class="d-flex align-center flex-wrap">
                                        <span class="font-weight-medium mr-2">{{ entity.name }}</span>
                                        <v-chip
                                            v-if="entity.metadata && entity.metadata.wikipedia_url"
                                            x-small
                                            outlined
                                            color="primary"
                                            class="mr-1 mb-1"
                                            @click.stop="openExternal(entity.metadata.wikipedia_url)">
                                            <v-icon x-small left>mdi-wikipedia</v-icon> Wikipedia
                                        </v-chip>
                                        <v-chip v-if="getEntityDomain(entity)" x-small outlined class="mr-1 mb-1" @click.stop="openExternal('https://' + getEntityDomain(entity))">
                                            <v-icon x-small left>mdi-web</v-icon>
                                            {{ getEntityDomain(entity) }}
                                        </v-chip>
                                    </v-list-item-title>
                                    <v-list-item-subtitle class="caption grey--text text--darken-1">
                                        <span class="mr-2"><v-icon x-small class="mr-1">mdi-chart-bar</v-icon>{{ entity.count }} response{{ entity.count === 1 ? "" : "s" }}</span>
                                    </v-list-item-subtitle>
                                </v-list-item-content>
                                <v-list-item-action class="entity-search-row__action">
                                    <v-chip v-if="isEntityAlreadyManaged(entity.name)" small outlined color="grey"> <v-icon left small>mdi-check-circle-outline</v-icon> Managed </v-chip>
                                    <v-btn v-else small rounded color="primary" class="white--text" @click.stop="whitelistEntityAsBrand(entity)" title="Add this entity as a new brand">
                                        <v-icon left small>mdi-plus</v-icon> Add as brand
                                    </v-btn>
                                </v-list-item-action>
                            </v-list-item>
                        </v-list>
                    </v-card>
                </div>
            </v-card-text>

            <v-divider />
            <v-card-actions class="px-6 py-3">
                <v-spacer />
                <v-btn text @click="closeDialog">Close</v-btn>
            </v-card-actions>
        </v-card>

        <v-dialog v-model="addAllAsBrandDialog" max-width="560px">
            <v-card>
                <v-card-title class="text-subtitle-1 font-weight-medium">Add all as one brand</v-card-title>
                <v-card-text>
                    <v-text-field
                        v-model="addAllAsBrandName"
                        dense
                        outlined
                        autofocus
                        hide-details
                        prepend-inner-icon="mdi-shape-outline"
                        label="Canonical brand name"
                        @keyup.enter="confirmAddAllAsBrand" />
                    <div class="caption grey--text text--darken-1 mt-3 mb-1">{{ addAllAsBrandMentions.length }} mention{{ addAllAsBrandMentions.length === 1 ? "" : "s" }} will be added:</div>
                    <div class="d-flex flex-wrap" style="max-height: 220px; overflow-y: auto">
                        <v-chip v-for="m in addAllAsBrandMentions" :key="`add-all-mention-${m}`" x-small outlined class="mr-1 mb-1">
                            {{ m }}
                        </v-chip>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="cancelAddAllAsBrand">Cancel</v-btn>
                    <v-btn color="primary" rounded :disabled="!addAllAsBrandName.trim()" @click="confirmAddAllAsBrand">Add brand</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
    export default {
        name: "SearchResponseEntitiesDialog",
        props: {
            value: {
                type: Boolean,
                default: false
            },
            projectId: {
                type: String,
                default: null
            },
            managedBrands: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                entitySearchQuery: "",
                entitySearchResults: [],
                entitySearchLoading: false,
                entitySearchError: "",
                entitySearchTimer: null,
                entitySearchRequestId: 0,
                addAllAsBrandDialog: false,
                addAllAsBrandName: "",
                addAllAsBrandMentions: []
            }
        },
        computed: {
            dialogProxy: {
                get() {
                    return this.value
                },
                set(v) {
                    this.$emit("input", v)
                }
            },
            addableEntityResults() {
                return (this.entitySearchResults || []).filter((e) => !this.isEntityAlreadyManaged(e?.name))
            },
            alreadyManagedCount() {
                return (this.entitySearchResults || []).filter((e) => this.isEntityAlreadyManaged(e?.name)).length
            },
            visibleResults() {
                const list = [...(this.entitySearchResults || [])]
                return list.sort((a, b) => {
                    const diff = (Number(b?.count) || 0) - (Number(a?.count) || 0)
                    if (diff !== 0) return diff
                    return this.normalizeMention(a?.name).localeCompare(this.normalizeMention(b?.name))
                })
            }
        },
        watch: {
            value(next) {
                if (next) {
                    this.resetSearch()
                } else {
                    this.cancelAddAllAsBrand()
                }
            },
            entitySearchQuery() {
                this.scheduleEntitySearch()
            }
        },
        methods: {
            closeDialog() {
                this.$emit("input", false)
            },
            getEntityDomain(entity) {
                return this.normalizeDomain(entity?.metadata?.officialWebsite || entity?.metadata?.website || "")
            },
            openExternal(url) {
                if (!url) return
                const safe = String(url)
                if (!/^https?:\/\//i.test(safe)) return
                window.open(safe, "_blank", "noopener,noreferrer")
            },
            normalizeMention(value) {
                return String(value || "").trim()
            },
            normalizeDomain(value) {
                return String(value || "")
                    .trim()
                    .toLowerCase()
                    .replace(/^https?:\/\//, "")
                    .replace(/^www\./, "")
                    .split(/[/?#]/)[0]
            },
            resetSearch() {
                if (this.entitySearchTimer) {
                    clearTimeout(this.entitySearchTimer)
                    this.entitySearchTimer = null
                }
                this.entitySearchQuery = ""
                this.entitySearchResults = []
                this.entitySearchLoading = false
                this.entitySearchError = ""
                this.entitySearchRequestId++
            },
            scheduleEntitySearch() {
                if (this.entitySearchTimer) {
                    clearTimeout(this.entitySearchTimer)
                    this.entitySearchTimer = null
                }
                const query = (this.entitySearchQuery || "").trim()
                if (!query || query.length < 2) {
                    this.entitySearchResults = []
                    this.entitySearchError = ""
                    this.entitySearchLoading = false
                    return
                }
                this.entitySearchLoading = true
                this.entitySearchError = ""
                this.entitySearchTimer = setTimeout(() => {
                    this.runEntitySearch(query)
                }, 350)
            },
            getMonitoringApiBaseUrl() {
                try {
                    const proxyIds = JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS || "[]")
                    const decoded = this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    return proxyIds.indexOf(decoded) === -1 ? process.env.VUE_APP_MONITORING_API_URL : process.env.VUE_APP_APIURL + "/monitoring-api"
                } catch {
                    return process.env.VUE_APP_MONITORING_API_URL
                }
            },
            async runEntitySearch(query) {
                if (!this.projectId) {
                    this.entitySearchError = "Project not available."
                    this.entitySearchLoading = false
                    return
                }
                const requestId = ++this.entitySearchRequestId
                try {
                    const url = this.getMonitoringApiBaseUrl() + "/ai-suite/entities/search"
                    const post_data = {
                        company_id: localStorage.company_id,
                        project_id: this.projectId,
                        query,
                        limit: 50
                    }
                    const resp = await this.$helpers.axiosPost(url, post_data)
                    if (requestId !== this.entitySearchRequestId) return
                    const entities = Array.isArray(resp?.data?.entities) ? resp.data.entities : []
                    this.entitySearchResults = entities
                    this.entitySearchError = ""
                } catch (err) {
                    if (requestId !== this.entitySearchRequestId) return
                    console.error("entity search error", err)
                    this.entitySearchResults = []
                    this.entitySearchError = "Failed to search entities."
                } finally {
                    if (requestId === this.entitySearchRequestId) {
                        this.entitySearchLoading = false
                    }
                }
            },
            isEntityAlreadyManaged(entityName) {
                const token = this.normalizeMention(entityName).toLowerCase()
                if (!token) return false
                return (this.managedBrands || []).some((b) => {
                    if (this.normalizeMention(b?.canonicalName).toLowerCase() === token) return true
                    const mentions = Array.isArray(b?.mentions) ? b.mentions : []
                    return mentions.some((m) => this.normalizeMention(m).toLowerCase() === token)
                })
            },
            buildBrandFromEntity(entity) {
                const name = this.normalizeMention(entity?.name)
                if (!name) return null
                const domainCandidate = this.normalizeDomain(entity?.metadata?.officialWebsite || entity?.metadata?.website || entity?.metadata?.wikipedia_url || "")
                return {
                    canonicalName: name,
                    mentions: [name],
                    blacklisted: [],
                    brand_domain: domainCandidate || "",
                    responsesWithMention: Number(entity?.count) || 0
                }
            },
            whitelistEntityAsBrand(entity) {
                const brand = this.buildBrandFromEntity(entity)
                if (!brand) return
                if (this.isEntityAlreadyManaged(brand.canonicalName)) return
                this.$emit("add-brands", [brand])
            },
            whitelistAllEntitiesAsBrands() {
                const brands = (this.addableEntityResults || []).map((e) => this.buildBrandFromEntity(e)).filter(Boolean)
                if (!brands.length) return
                this.$emit("add-brands", brands)
            },
            openAddAllAsBrandDialog() {
                const mentions = (this.addableEntityResults || []).map((e) => this.normalizeMention(e?.name)).filter(Boolean)
                if (!mentions.length) return
                this.addAllAsBrandMentions = Array.from(new Set(mentions))
                this.addAllAsBrandName = this.addAllAsBrandMentions[0] || ""
                this.addAllAsBrandDialog = true
            },
            cancelAddAllAsBrand() {
                this.addAllAsBrandDialog = false
                this.addAllAsBrandName = ""
                this.addAllAsBrandMentions = []
            },
            confirmAddAllAsBrand() {
                const canonical = this.normalizeMention(this.addAllAsBrandName)
                if (!canonical) return
                const mentions = Array.from(new Set([canonical, ...(this.addAllAsBrandMentions || []).map((m) => this.normalizeMention(m)).filter(Boolean)]))
                const totalCount = (this.addableEntityResults || []).reduce((sum, e) => sum + (Number(e?.count) || 0), 0)

                this.$emit("add-merged-brand", {
                    canonicalName: canonical,
                    mentions,
                    responsesWithMention: totalCount
                })
                this.cancelAddAllAsBrand()
            }
        }
    }
</script>

<style scoped>
    .entity-search-card__header {
        background: #fafafa;
        border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        gap: 4px 0;
    }
    .entity-search-list {
        max-height: 420px;
        overflow-y: auto;
    }
    .entity-search-row {
        min-height: 56px;
    }
    .entity-search-row + .entity-search-row {
        border-top: 1px solid rgba(0, 0, 0, 0.06);
    }
    .entity-search-row--managed {
        background: #fafafa;
    }
    .entity-search-row__action {
        margin: 0 !important;
        align-self: center;
    }
</style>
